import React, { useState, useEffect, useCallback } from 'react';
import { apiService } from './apiService';
import { csvExport, EXPORT_COLUMNS } from './csvUtils';
import './ActionsView.css';
import EmailComposer from './EmailComposer';
import SnoozeModal from './SnoozeModal';
import useIsMobile from './useIsMobile';

const API = process.env.REACT_APP_API_URL || '';

// ── Constants ────────────────────────────────────────────────────────────────



const PRIORITY_COLORS = {
  critical: '#dc2626',
  high:     '#ef4444',
  medium:   '#f59e0b',
  low:      '#10b981',
};

const STRAP_ENTITY_CONFIG = {
  deal:           { icon: '💼', color: '#4f46e5', bg: '#eef2ff', label: 'Deal' },
  prospect:       { icon: '🎯', color: '#0F9D8E', bg: '#f0fdfa', label: 'Prospect' },
  account:        { icon: '🏢', color: '#7c3aed', bg: '#f5f3ff', label: 'Account' },
  implementation: { icon: '🚀', color: '#0369a1', bg: '#f0f9ff', label: 'Implementation' },
};

const STRAP_SECTIONS = [
  { key: 'S', field: 'situation',   label: 'Hurdle',      color: '#3b82f6' },
  { key: 'T', field: 'target',      label: 'Goal',        color: '#10b981' },
  { key: 'R', field: 'response',    label: 'Strategy',    color: '#f59e0b' },
  { key: 'A', field: 'action_plan', label: 'Action Plan', color: '#8b5cf6' },
];

const STATUS_CONFIG = {
  not_started: { label: 'Not Started', icon: '○', color: '#6b7280', next: 'in_progress' },
  blocked:     { label: 'Blocked',      icon: '⊘', color: '#dc2626', next: 'in_progress' },
  skipped:     { label: 'Skipped',      icon: '⤼', color: '#9ca3af', next: null },
  cancelled:   { label: 'Cancelled',    icon: '✕', color: '#9ca3af', next: null },
  in_progress:  { label: 'In Progress',  icon: '◑', color: '#3b82f6', next: 'completed'  },
  completed:    { label: 'Completed',    icon: '●', color: '#10b981', next: null          },
};

const SNOOZE_DURATION_LABELS = {
  '1_week':      '1 week',
  '2_weeks':     '2 weeks',
  '1_month':     '1 month',
  'stage_change':'until stage changes',
  'indefinite':  'indefinitely',
};

const SOURCE_RULE_LABELS = {
  stagnant_deal:             'Stagnant Deal',
  high_value_no_meeting:     'High Value — No Meeting',
  stage_proposal_followup:   'Proposal Follow-up',
  decision_maker_no_contact: 'Decision Maker — No Contact',
  unanswered_email:          'Unanswered Email',
  no_proposal_doc:           'No Proposal Document',
  champion_nurture:          'Champion Nurture',
  no_files:                  'No Files Uploaded',
  ai_enhancer:               'AI Enhancement',
};

const MANUAL_LOG_CONFIG = {
  call:          { icon: '📞', label: 'Log Call',          placeholder: 'What was discussed? Any next steps agreed?',    resourceLabel: 'Call',       opensExternal: true },
  whatsapp:      { icon: '💬', label: 'Log WhatsApp',      placeholder: 'What was the outcome of the conversation?',     resourceLabel: 'WhatsApp',   opensExternal: true },
  linkedin:      { icon: '🔗', label: 'Log LinkedIn',      placeholder: 'What message did you send / receive?',          resourceLabel: 'LinkedIn',   opensExternal: true },
  slack:         { icon: '💬', label: 'Log Slack Message', placeholder: 'What was the key point of the exchange?',       resourceLabel: 'Slack',      opensExternal: false },
  document:      { icon: '📄', label: 'Log Document Sent', placeholder: 'Which document? Any response from the prospect?', resourceLabel: 'Files',    navigateTab: 'files' },
  internal_task: { icon: '🔧', label: 'Log Internal Task', placeholder: 'What did you complete or decide?',             resourceLabel: 'Task',       opensExternal: false },
};

// ── Helpers ──────────────────────────────────────────────────────────────────

function apiFetch(path, options = {}) {
  const token = localStorage.getItem('token') || localStorage.getItem('authToken');
  // ...options is spread BEFORE headers so a caller passing options.headers
  // merges into (rather than replaces) the auth + content-type defaults.
  return fetch(`${API}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...(options.headers || {}),
    },
  }).then(r => {
    if (!r.ok) return r.json().then(e => Promise.reject(new Error(e?.error?.message || r.statusText)));
    return r.json();
  });
}

async function apiFetchRaw(path, options = {}) {
  const token = localStorage.getItem('token') || localStorage.getItem('authToken');
  const res = await fetch(`${API}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...(options.headers || {}),
    },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data?.error?.message || res.statusText);
  return data;
}

function formatDate(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  const now = new Date();
  const diffDays = Math.ceil((d - now) / (1000 * 60 * 60 * 24));
  if (diffDays < 0)   return { text: `${Math.abs(diffDays)}d overdue`, overdue: true };
  if (diffDays === 0) return { text: 'Due today', today: true };
  if (diffDays === 1) return { text: 'Due tomorrow' };
  return { text: `Due ${d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}` };
}

function nextStepLabel(nextStep) {
  const map = {
    email:         '✉️ Email',
    call:          '📞 Call',
    whatsapp:      '💬 WhatsApp',
    linkedin:      '🔗 LinkedIn',
    slack:         '💬 Slack',
    document:      '📄 Document',
    contract:      '📄 Contract',
    internal_task: '🔧 Internal Task',
  };
  return map[nextStep] || '✉️ Email';
}

// ── Evidence Panel ────────────────────────────────────────────────────────────

function EvidencePanel({ action }) {
  const [open, setOpen] = useState(false);
  const hasEvidence = action.description || action.context || action.suggestedAction || action.evidenceEmail;
  if (!hasEvidence) return null;

  const ruleLabel = action.sourceRule
    ? (SOURCE_RULE_LABELS[action.sourceRule] || action.sourceRule)
    : null;

  return (
    <div className="av-evidence">
      <button className="av-evidence-toggle" onClick={() => setOpen(o => !o)}>
        <span>Why this action?</span>
        <span className="av-evidence-chevron">{open ? '▲' : '▼'}</span>
      </button>
      {open && (
        <div className="av-evidence-body">
          {ruleLabel && (
            <div className="av-evidence-row">
              <span className="av-evidence-label">Rule</span>
              <span className="av-evidence-value av-evidence-rule">{ruleLabel}</span>
            </div>
          )}
          {action.description && (
            <div className="av-evidence-row">
              <span className="av-evidence-label">Signal</span>
              <span className="av-evidence-value">{action.description}</span>
            </div>
          )}
          {action.context && (
            <div className="av-evidence-row">
              <span className="av-evidence-label">AI Insight</span>
              <span className="av-evidence-value av-evidence-ai">{action.context}</span>
            </div>
          )}
          {action.evidenceEmail && (
            <div className="av-evidence-email">
              <div className="av-evidence-email-header">
                <span className="av-evidence-label">Triggering Email</span>
                <span className={`av-evidence-email-dir ${action.evidenceEmail.direction}`}>
                  {action.evidenceEmail.direction === 'sent' ? '↑ Sent' : '↓ Received'}
                </span>
              </div>
              <div className="av-evidence-email-subject">"{action.evidenceEmail.subject}"</div>
              {action.evidenceEmail.snippet && (
                <div className="av-evidence-email-snippet">{action.evidenceEmail.snippet}</div>
              )}
            </div>
          )}
          {action.suggestedAction && (
            <div className="av-evidence-row av-evidence-suggested">
              <span className="av-evidence-label">Suggested</span>
              <span className="av-evidence-value">{action.suggestedAction}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Resume Button ─────────────────────────────────────────────────────────────
// Appears only on in_progress actions. Resolves the best deep-link target for
// the action based on action_type, source_rule, and deal_id, then fires the
// appropriate navigation event.

async function resolveResumeTarget(action) {
  const token = localStorage.getItem('token') || localStorage.getItem('authToken');
  const API   = process.env.REACT_APP_API_URL || '';

  const type      = (action.actionType  || action.action_type  || '').toLowerCase();
  const rule      = (action.sourceRule  || action.source_rule  || '').toLowerCase();
  const nextStep  = (action.nextStep    || action.next_step    || '').toLowerCase();
  const dealId    = action.deal?.id || action.dealId    || action.deal_id    || null;

  // Helper: fetch with auth
  const get = async (path) => {
    const r = await fetch(`${API}${path}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!r.ok) return null;
    return r.json();
  };

  // ── Email-type actions → most recent email thread for the deal ──────────
  if (
    type === 'unanswered_email' ||
    rule === 'unanswered_email' ||
    type === 'email_send' ||
    type === 'email' ||
    (type === 'follow_up' && nextStep === 'email') ||
    nextStep === 'email'
  ) {
    return { tab: 'email', dealId, label: 'Open Email Thread', icon: '✉️' };
  }

  // ── Meeting prep / followup → calendar or deal ──────────────────────────
  if (type === 'meeting_prep' || rule === 'meeting_prep') {
    // Try to find the upcoming/most recent meeting
    if (dealId) {
      const data = await get(`/meetings?deal_id=${dealId}`).catch(() => null);
      const meetings = data?.meetings || [];
      const upcoming = meetings
        .filter(m => m.status === 'scheduled' && new Date(m.start_time) > new Date())
        .sort((a, b) => new Date(a.start_time) - new Date(b.start_time))[0];
      if (upcoming) return { tab: 'calendar', meetingId: upcoming.id, label: 'View Meeting', icon: '📅' };
    }
    return { tab: 'deals', dealId, label: 'Open Deal', icon: '💼' };
  }

  if (type === 'meeting_followup' || rule === 'meeting_followup') {
    return { tab: 'deals', dealId, label: 'Open Deal', icon: '💼' };
  }

  // ── Meeting schedule → calendar (scheduling new) ─────────────────────────
  if (type === 'meeting_schedule' || type === 'meeting') {
    return { tab: 'calendar', label: 'Schedule Meeting', icon: '📅' };
  }

  // ── Document / file actions → Files tab ─────────────────────────────────
  if (
    type === 'document_prep' || type === 'document' ||
    rule === 'no_proposal_doc' || rule === 'no_files' ||
    rule === 'health_5a_competitive' || rule === 'failed_file'
  ) {
    return { tab: 'files', dealId, label: 'Open Files', icon: '📁' };
  }

  // ── Contract actions → Contracts module ────────────────────────────────
  if (
    type === 'contract' || type === 'contract_review' || type === 'contract_sign' ||
    rule === 'contract_expiring' || rule === 'contract_approval' ||
    nextStep === 'contract'
  ) {
    const contractId = action.contractId || action.contract_id || null;
    return { tab: 'contracts', contractId, dealId, label: 'Open Contract', icon: '📄' };
  }

  // ── Internal / task → Deal detail pane ──────────────────────────────────
  if (dealId) return { tab: 'deals', dealId, label: 'Open Deal', icon: '💼' };

  // Fallback — go to Actions
  return { tab: 'actions', label: 'Back to Actions', icon: '⚡' };
}

function ResumeButton({ action }) {
  const [loading, setLoading] = useState(false);

  if (action.status !== 'in_progress') return null;

  async function handleResume(e) {
    e.stopPropagation();
    setLoading(true);
    try {
      const target = await resolveResumeTarget(action);

      // Single enriched navigate event — carries tab AND dealId together so
      // App.js can set pendingEmailDealId/pendingDealId BEFORE switching the
      // tab. This prevents the race condition where EmailView mounted with
      // dealId=null because the two separate events (resumeToEmail + navigate)
      // triggered separate async React state updates.
      window.dispatchEvent(new CustomEvent('navigate', {
        detail: {
          tab:        target.tab,
          dealId:     target.dealId     || null,
          contractId: target.contractId || null,
          resume:     true,
        },
      }));
    } catch (err) {
      console.error('Resume navigation failed:', err);
    } finally {
      setLoading(false);
    }
  }

  return (
    <button
      className="av-resume-btn"
      onClick={handleResume}
      disabled={loading}
      title="Pick up where you left off"
    >
      {loading ? '…' : '↩ Resume'}
    </button>
  );
}

// ── Manual Log Modal ──────────────────────────────────────────────────────────

// ── Export Context Button ─────────────────────────────────────────────────────
// Generates a structured plain-text export of everything relevant to this action.
// The rep can paste it into any AI tool (ChatGPT, Claude.ai, etc.) for feedback.

function ExportContextButton({ action }) {
  const [copied, setCopied] = useState(false);

  function buildExport() {
    const lines = [];
    lines.push('# CRM Action Context Export');
    lines.push(`Generated: ${new Date().toLocaleString()}`);
    lines.push('');

    lines.push('## Action');
    lines.push(`Title: ${action.title}`);
    lines.push(`Type: ${action.actionType || action.type || 'N/A'}`);
    lines.push(`Channel: ${action.nextStep || 'N/A'}`);
    lines.push(`Priority: ${action.priority}`);
    lines.push(`Status: ${action.status}`);
    lines.push(`Due: ${action.dueDate ? new Date(action.dueDate).toLocaleDateString() : 'N/A'}`);
    if (action.description) lines.push(`\nContext: ${action.description}`);
    if (action.suggestedAction) lines.push(`\nSuggested Approach: ${action.suggestedAction}`);
    lines.push('');

    if (action.deal) {
      lines.push('## Deal');
      lines.push(`Name: ${action.deal.name}`);
      lines.push(`Value: $${parseFloat(action.deal.value || 0).toLocaleString()}`);
      lines.push(`Stage: ${action.deal.stage || 'N/A'}`);
      if (action.deal.account) lines.push(`Account: ${action.deal.account}`);
      lines.push('');
    }

    if (action.prospect) {
      lines.push('## Prospect');
      lines.push(`Name: ${action.prospect.firstName} ${action.prospect.lastName}`);
      if (action.prospect.companyName) lines.push(`Company: ${action.prospect.companyName}`);
      lines.push(`Stage: ${action.prospect.stage || 'N/A'}`);
      lines.push('');
    }

    if (action.contact) {
      lines.push('## Primary Contact');
      lines.push(`Name: ${action.contact.firstName} ${action.contact.lastName}`);
      if (action.contact.email) lines.push(`Email: ${action.contact.email}`);
      lines.push('');
    }

    if (action.evidenceEmail) {
      lines.push('## Recent Email Evidence');
      lines.push(`Direction: ${action.evidenceEmail.direction === 'sent' ? 'Sent by us' : 'Received'}`);
      lines.push(`Subject: ${action.evidenceEmail.subject || 'N/A'}`);
      if (action.evidenceEmail.snippet) lines.push(`Preview: ${action.evidenceEmail.snippet}`);
      lines.push('');
    }

    if (action.sourceRule) {
      lines.push('## Why This Action Was Generated');
      lines.push(`Rule: ${action.sourceRule.replace(/_/g, ' ')}`);
      if (action.healthParam) lines.push(`Health Parameter: ${action.healthParam}`);
      lines.push('');
    }

    lines.push('---');
    lines.push('Paste this into your preferred AI assistant and ask:');
    lines.push('"Based on this CRM context, what\'s the best approach for this action? What should I say/do and what outcome should I aim for?"');

    return lines.join('\n');
  }

  function handleCopy(e) {
    e.stopPropagation();
    const text = buildExport();
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }).catch(() => {
      // Fallback for browsers without clipboard API
      const el = document.createElement('textarea');
      el.value = text;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  return (
    <button
      className="av-export-btn"
      onClick={handleCopy}
      title="Copy action context to clipboard — paste into any AI tool for suggestions"
    >
      {copied ? '✓ Copied!' : '📋 Export'}
    </button>
  );
}

function ManualLogModal({ action, onComplete, onInProgress, onClose }) {
  const [notes, setNotes]   = useState('');
  const [saving, setSaving] = useState(false);
  const cfg = MANUAL_LOG_CONFIG[action.nextStep] || MANUAL_LOG_CONFIG.internal_task;
  const person = action.contact || action.prospect || {};
  const personName = [person.firstName, person.lastName].filter(Boolean).join(' ');
  const phone = person.phone;
  const linkedinUrl = person.linkedinUrl;
  const dealId = action.deal?.id || null;

  async function handleDone() {
    setSaving(true);
    try { await onComplete(notes); } finally { setSaving(false); }
  }

  async function handleInProgress() {
    setSaving(true);
    try { await onInProgress(notes); } finally { setSaving(false); }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content av-log-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{cfg.icon} {cfg.label}</h2>
          <button className="close-button" onClick={onClose}>×</button>
        </div>
        <div className="av-log-modal-body">
          <div className="av-log-action-title">{action.title}</div>

          {/* Resource links */}
          {personName && (
            <div className="av-log-resource-bar">
              <span className="av-log-resource-person">{personName}</span>
              {phone && (
                <a href={`tel:${phone.replace(/\s/g, '')}`} className="av-log-resource-link av-log-resource-link--call">
                  📞 {phone}
                </a>
              )}
              {linkedinUrl && (
                <a href={linkedinUrl.startsWith('http') ? linkedinUrl : `https://${linkedinUrl}`}
                   target="_blank" rel="noreferrer" className="av-log-resource-link av-log-resource-link--linkedin">
                  🔗 LinkedIn ↗
                </a>
              )}
              {phone && (
                <a href={`https://wa.me/${phone.replace(/[\s\-()]/g, '').replace(/^\+/, '')}`}
                   target="_blank" rel="noreferrer" className="av-log-resource-link av-log-resource-link--whatsapp">
                  💬 WhatsApp ↗
                </a>
              )}
              {person.email && (
                <a href={`mailto:${person.email}`} className="av-log-resource-link av-log-resource-link--email">
                  ✉️ {person.email}
                </a>
              )}
            </div>
          )}

          {action.nextStep === 'document' && dealId && (
            <button className="av-log-resource-nav" onClick={() => {
              window.dispatchEvent(new CustomEvent('navigate', {
                detail: { tab: 'deals', dealId, resume: true },
              }));
            }}>
              📁 Open Files for {action.deal?.name || 'this deal'} →
            </button>
          )}

          {action.nextStep === 'contract' && (
            <button className="av-log-resource-nav" style={{ borderColor: '#a78bfa', color: '#6d28d9' }} onClick={() => {
              const contractId = action.contractId || action.contract_id || null;
              window.dispatchEvent(new CustomEvent('navigate', {
                detail: { tab: 'contracts', contractId, dealId, resume: true },
              }));
            }}>
              📄 Open Contract{action.deal?.name ? ` for ${action.deal.name}` : ''} →
            </button>
          )}

          {action.suggestedAction && (
            <div className="av-log-suggested">
              <span className="av-log-suggested-label">Suggested approach</span>
              <p>{action.suggestedAction}</p>
            </div>
          )}
          <div className="form-group">
            <label htmlFor="log-notes">Notes (optional)</label>
            <textarea
              id="log-notes"
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder={cfg.placeholder}
              rows="5"
            />
          </div>
        </div>
        <div className="av-log-modal-footer">
          <button className="btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="av-log-btn-progress" onClick={handleInProgress} disabled={saving}>
            {saving ? '…' : '◑ Still in Progress'}
          </button>
          <button className="av-log-btn-done" onClick={handleDone} disabled={saving}>
            {saving ? '…' : '✓ Mark Done'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Status Stepper ────────────────────────────────────────────────────────────

function StatusStepper({ action, onStatusChange, onStart }) {
  const [updating, setUpdating] = useState(false);
  const cfg = STATUS_CONFIG[action.status] || STATUS_CONFIG.not_started;

  async function advance() {
    if (!cfg.next || updating) return;
    if (cfg.next === 'in_progress') {
      if (onStart) { onStart(action); return; }
    }
    setUpdating(true);
    try { await onStatusChange(refOf(action), cfg.next); }
    finally { setUpdating(false); }
  }

  // Snoozed actions don't show the stepper
  if (action.status === 'snoozed') return null;

  return (
    <div className="av-status">
      <div className="av-status-steps">
        {Object.entries(STATUS_CONFIG).map(([key, s]) => (
          <div
            key={key}
            className={`av-status-step ${action.status === key ? 'active' : ''} ${
              Object.keys(STATUS_CONFIG).indexOf(key) <
              Object.keys(STATUS_CONFIG).indexOf(action.status) ? 'done' : ''
            }`}
            style={{ '--step-color': s.color }}
          >
            <span className="av-status-dot">{s.icon}</span>
            <span className="av-status-step-label">{s.label}</span>
          </div>
        ))}
      </div>
      {cfg.next && (
        <button
          className={`av-status-btn av-status-btn--${cfg.next}`}
          onClick={advance}
          disabled={updating}
        >
          {updating ? '…' : cfg.next === 'in_progress'
            ? `${nextStepLabel(action.nextStep)} →`
            : 'Complete ✓'}
        </button>
      )}
      {action.status === 'completed' && (
        <span className="av-status-completed-by">
          {action.autoCompleted
            ? '🤖 Auto-completed'
            : `✓ Marked done${action.completedAt ? ` · ${new Date(action.completedAt).toLocaleDateString()}` : ''}`}
        </span>
      )}
    </div>
  );
}

// ── Column Definitions ──────────────────────────────────────────────────────

const ALL_COLUMNS = [
  { key: 'channel',  label: 'Channel',  width: 90,  defaultVisible: true,  sortable: true,  filterable: true  },
  { key: 'title',    label: 'Action',   width: 0,   defaultVisible: true,  sortable: true,  filterable: false },
  { key: 'deal',     label: 'Deal',     width: 160, defaultVisible: true,  sortable: true,  filterable: true  },
  { key: 'contact',  label: 'Contact',  width: 130, defaultVisible: true,  sortable: true,  filterable: false },
  { key: 'due',      label: 'Due',      width: 100, defaultVisible: true,  sortable: true,  filterable: false },
  { key: 'priority', label: 'Priority', width: 90,  defaultVisible: true,  sortable: true,  filterable: true  },
  { key: 'status',   label: 'Status',   width: 110, defaultVisible: true,  sortable: true,  filterable: true  },
  { key: 'source',   label: 'Source',   width: 80,  defaultVisible: true,  sortable: true,  filterable: true  },
  { key: 'account',  label: 'Account',  width: 130, defaultVisible: false, sortable: true,  filterable: true  },
  { key: 'stage',    label: 'Stage',    width: 100, defaultVisible: false, sortable: true,  filterable: true  },
  { key: 'internal', label: 'Type',     width: 80,  defaultVisible: false, sortable: true,  filterable: true  },
  { key: 'module',   label: 'Module',   width: 110, defaultVisible: false, sortable: true,  filterable: true  },
  { key: 'actions',  label: '',         width: 60,  defaultVisible: true,  sortable: false, filterable: false },
];

const CHANNEL_META = {
  email:         { icon: '✉️', label: 'Email',     color: '#f59e0b' },
  call:          { icon: '📞', label: 'Call',       color: '#10b981' },
  follow_up:     { icon: '✉️', label: 'Follow Up', color: '#f59e0b' },
  linkedin:      { icon: '🔗', label: 'LinkedIn',   color: '#2563eb' },
  whatsapp:      { icon: '💬', label: 'WhatsApp',   color: '#16a34a' },
  document:      { icon: '📄', label: 'Document',   color: '#8b5cf6' },
  slack:         { icon: '💬', label: 'Slack',       color: '#e11d48' },
  internal_task: { icon: '🔧', label: 'Task',       color: '#6b7280' },
};

const SOURCE_META = {
  strap:           { label: '🎯 STRAP',   bg: '#faf5ff', color: '#7c3aed', border: '#e9d5ff' },
  ai_generated:    { label: '🤖 AI',      bg: '#f0fdf4', color: '#15803d', border: '#bbf7d0' },
  playbook:        { label: '📘 Play',    bg: '#eff6ff', color: '#2563eb', border: '#bfdbfe' },
  auto_generated:  { label: '⚙️ Rules',   bg: '#fefce8', color: '#a16207', border: '#fde68a' },
  // System / non-AI sources. Without explicit entries these fell through to the
  // default below, which used to be ai_generated — mislabeling system actions as "AI".
  sequence_send_failed:     { label: '⚠️ System', bg: '#fef2f2', color: '#b91c1c', border: '#fecaca' },
  linkedin_autosend_failed: { label: '⚠️ System', bg: '#fef2f2', color: '#b91c1c', border: '#fecaca' },
  linkedin_refresh_nudge:   { label: '🔗 LinkedIn', bg: '#f1f5f9', color: '#475569', border: '#cbd5e1' },
  sla_sweep:       { label: '⏱ SLA',      bg: '#f1f5f9', color: '#475569', border: '#cbd5e1' },
  crm_sync:        { label: '🔄 Sync',     bg: '#f1f5f9', color: '#475569', border: '#cbd5e1' },
  manual:          { label: '✋ Manual',   bg: '#f1f5f9', color: '#475569', border: '#cbd5e1' },
};

// Neutral fallback for any source not explicitly mapped above — must NOT be the
// AI badge, or unrecognized system sources misreport as AI-generated.
const SOURCE_DEFAULT = { label: '⚙️ System', bg: '#f1f5f9', color: '#475569', border: '#cbd5e1' };

const MODULE_META = {
  deals:       { icon: '💼', label: 'Deals',       color: '#4f46e5', bg: '#eef2ff' },
  prospecting: { icon: '🎯', label: 'Prospecting', color: '#0F9D8E', bg: '#f0fdfa' },
  contracts:   { icon: '📄', label: 'Contracts',   color: '#7c3aed', bg: '#f5f3ff' },
  handovers:   { icon: '🤝', label: 'Projects',    color: '#0369a1', bg: '#f0f9ff' },
  service:     { icon: '🎧', label: 'Service',     color: '#065f46', bg: '#d1fae5' },
  agency:      { icon: '🏢', label: 'Agency',      color: '#92400e', bg: '#fef3c7' },
  general:     { icon: '⚡', label: 'General',     color: '#6b7280', bg: '#f3f4f6' },
};

const STATUS_META = {
  not_started: { icon: '○', label: 'To Start',  color: '#9ca3af' },
  blocked:     { icon: '⊘', label: 'Blocked',   color: '#dc2626' },
  skipped:     { icon: '⤼', label: 'Skipped',   color: '#9ca3af' },
  cancelled:   { icon: '✕', label: 'Cancelled', color: '#9ca3af' },
  in_progress:  { icon: '◑', label: 'In Progress', color: '#f59e0b' },
  completed:    { icon: '●', label: 'Done',        color: '#10b981' },
  snoozed:      { icon: '😴', label: 'Snoozed', color: '#8b5cf6' },
};

function tableDueLabel(d) {
  if (!d) return { text: '—', overdue: false, today: false };
  const now = new Date(); now.setHours(0,0,0,0);
  const due = new Date(d); due.setHours(0,0,0,0);
  const diff = Math.ceil((due - now) / 86400000);
  if (diff < 0) return { text: `${Math.abs(diff)}d overdue`, overdue: true };
  if (diff === 0) return { text: 'Today', today: true };
  if (diff === 1) return { text: 'Tomorrow' };
  return { text: due.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) };
}

function getColumnValue(action, colKey) {
  switch (colKey) {
    case 'channel':  return action.nextStep || 'email';
    case 'title':    return action.title || '';
    case 'deal':     return action.deal?.name || '';
    case 'contact':  return action.contact ? `${action.contact.firstName || ''} ${action.contact.lastName || ''}`.trim() : (action.prospect ? `${action.prospect.firstName || ''} ${action.prospect.lastName || ''}`.trim() : '');
    case 'due':      return action.dueDate || '';
    case 'priority': return action.priority || 'medium';
    case 'status':   return action.status || 'not_started';
    case 'source':   return action.source || '';
    case 'account':  return action.deal?.account || action.prospect?.companyName || '';
    case 'stage':    return action.deal?.stage || action.prospect?.stage || '';
    case 'internal': return action.isInternal ? 'Internal' : 'External';
    case 'module':   return MODULE_META[action.sourceModule]?.label || 'General';
    default:         return '';
  }
}

// ── Actions Table Component ─────────────────────────────────────────────────

function ActionsTable({ actions, onStatusChange, onStart, onSnoozeClick, onUnsnooze, visibleCols, setVisibleCols }) {
  const [sortKey, setSortKey] = useState('due');
  const [sortDir, setSortDir] = useState('asc');
  const [colFilters, setColFilters] = useState({});
  const [showColConfig, setShowColConfig] = useState(false);

  // Unique values for filterable columns
  const filterOptions = {};
  ALL_COLUMNS.filter(c => c.filterable).forEach(col => {
    const vals = new Set();
    actions.forEach(a => {
      const v = getColumnValue(a, col.key);
      if (v) vals.add(v);
    });
    filterOptions[col.key] = Array.from(vals).sort();
  });

  // Apply column-level filters
  let filtered = actions;
  Object.entries(colFilters).forEach(([key, val]) => {
    if (val) {
      filtered = filtered.filter(a => getColumnValue(a, key) === val);
    }
  });

  // Sort
  const sorted = [...filtered].sort((a, b) => {
    let aVal = getColumnValue(a, sortKey);
    let bVal = getColumnValue(b, sortKey);

    // Priority sort order
    if (sortKey === 'priority') {
      const order = { critical: 1, high: 2, medium: 3, low: 4 };
      aVal = order[aVal] || 3;
      bVal = order[bVal] || 3;
      return sortDir === 'asc' ? aVal - bVal : bVal - aVal;
    }
    // Status sort order
    if (sortKey === 'status') {
      const order = { blocked: 1, in_progress: 2, not_started: 3, snoozed: 4, completed: 5, skipped: 6, cancelled: 7 };
      aVal = order[aVal] || 1;
      bVal = order[bVal] || 1;
      return sortDir === 'asc' ? aVal - bVal : bVal - aVal;
    }
    // Date sort
    if (sortKey === 'due') {
      const da = aVal ? new Date(aVal).getTime() : 9999999999999;
      const db = bVal ? new Date(bVal).getTime() : 9999999999999;
      return sortDir === 'asc' ? da - db : db - da;
    }
    // String sort
    aVal = String(aVal).toLowerCase();
    bVal = String(bVal).toLowerCase();
    if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });

  function handleSort(key) {
    if (sortKey === key) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  }

  function toggleCol(key) {
    setVisibleCols(prev =>
      prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
    );
  }

  const activeCols = ALL_COLUMNS.filter(c => visibleCols.includes(c.key));

  // Navigation helpers for clickable cells
  function navToDeal(action) {
    const dealId = action.deal?.id;
    if (dealId) {
      window.dispatchEvent(new CustomEvent('navigate', {
        detail: { tab: 'deals', dealId, resume: true },
      }));
    }
  }

  function navToContact(action) {
    const contactId = action.contact?.id;
    if (contactId) {
      window.dispatchEvent(new CustomEvent('navigate', {
        detail: { tab: 'deals', dealId: action.deal?.id, contactId, resume: true },
      }));
    } else if (action.prospect?.id) {
      window.dispatchEvent(new CustomEvent('navigate', {
        detail: { tab: 'prospecting' },
      }));
    }
  }

  function navToAction(action) {
    // Same as clicking the Start button — routes to the right resource
    onStart(action);
  }

  async function navToInProgress(action) {
    if (action.status !== 'in_progress') return;
    // Use resolveResumeTarget to find where to go
    try {
      const target = await resolveResumeTarget(action);
      window.dispatchEvent(new CustomEvent('navigate', {
        detail: {
          tab: target.tab,
          dealId: target.dealId || null,
          resume: true,
        },
      }));
    } catch (err) {
      console.error('Navigate to in-progress failed:', err);
    }
  }
  const gridCols = activeCols.map(c => c.width ? `${c.width}px` : '1fr').join(' ');

  return (
    <div className="av-table-root">
      {/* Column config toggle — fixed at top */}
      <div className="av-table-toolbar">
        <span className="av-table-result-count">{sorted.length} action{sorted.length !== 1 ? 's' : ''}</span>
        {Object.keys(colFilters).some(k => colFilters[k]) && (
          <button className="av-table-clear-filters" onClick={() => setColFilters({})}>
            ✕ Clear column filters
          </button>
        )}
        <button
          className="av-table-col-config-btn"
          onClick={() => setShowColConfig(p => !p)}
        >
          ⚙️ Columns
        </button>
        {showColConfig && (
          <div className="av-table-col-config">
            {ALL_COLUMNS.filter(c => c.key !== 'actions').map(col => (
              <label key={col.key} className="av-table-col-config-item">
                <input
                  type="checkbox"
                  checked={visibleCols.includes(col.key)}
                  onChange={() => toggleCol(col.key)}
                />
                {col.label}
              </label>
            ))}
          </div>
        )}
      </div>

      {/* Header row — fixed at top */}
      <div className="av-table-header" style={{ gridTemplateColumns: gridCols }}>
        {activeCols.map(col => {
          const isSorted = sortKey === col.key;
          const arrow = isSorted ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '';
          return (
            <div key={col.key} className="av-table-th">
              {col.sortable ? (
                <span
                  className={`av-table-th-label ${isSorted ? 'av-table-th-label--active' : ''}`}
                  onClick={() => handleSort(col.key)}
                >
                  {col.label}{arrow}
                </span>
              ) : (
                <span className="av-table-th-label">{col.label}</span>
              )}
              {col.filterable && filterOptions[col.key]?.length > 1 && (
                <select
                  className="av-table-th-filter"
                  value={colFilters[col.key] || ''}
                  onChange={e => setColFilters(p => ({ ...p, [col.key]: e.target.value }))}
                >
                  <option value="">All</option>
                  {filterOptions[col.key].map(v => (
                    <option key={v} value={v}>{col.key === 'channel' ? (CHANNEL_META[v]?.label || v) : col.key === 'source' ? (SOURCE_META[v]?.label || v).replace(/[^\w\s]/g, '').trim() : col.key === 'status' ? (STATUS_META[v]?.label || v) : col.key === 'priority' ? v.charAt(0).toUpperCase() + v.slice(1) : v}</option>
                  ))}
                </select>
              )}
            </div>
          );
        })}
      </div>

      {/* Scrollable rows */}
      <div className="av-table-body">
        {sorted.map((action, i) => {
        const ch = CHANNEL_META[action.nextStep] || CHANNEL_META.email;
        const pri = PRIORITY_COLORS[action.priority] || PRIORITY_COLORS.medium;
        const st = STATUS_META[action.status] || STATUS_META.not_started;
        const due = tableDueLabel(action.dueDate);
        const src = SOURCE_META[action.source] || SOURCE_DEFAULT;
        const isComplete = action.status === 'completed';

        return (
          <div
            key={refOf(action)}
            className={`av-table-row ${isComplete ? 'av-table-row--completed' : ''} ${i % 2 === 0 ? 'av-table-row--even' : ''}`}
            style={{ gridTemplateColumns: gridCols }}
          >
            {activeCols.map(col => {
              switch (col.key) {
                case 'channel':
                  return (
                    <div key={col.key} className="av-table-cell">
                      <span className="av-table-channel-badge" style={{ background: ch.color + '14', color: ch.color }}>
                        {ch.icon} {ch.label}
                      </span>
                    </div>
                  );
                case 'title': {
                  const mod = MODULE_META[action.sourceModule] || MODULE_META.general;
                  return (
                    <div key={col.key} className="av-table-cell av-table-cell--title av-table-cell--clickable" onClick={() => navToAction(action)} title="Start this action">
                      {action.isInternal && <span className="av-table-internal-icon">🏠</span>}
                      <span style={{
                        fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 8,
                        background: mod.bg, color: mod.color,
                        marginRight: 6, flexShrink: 0, whiteSpace: 'nowrap',
                      }}>
                        {mod.icon} {mod.label}
                      </span>
                      <span className={isComplete ? 'av-table-strikethrough' : ''}>{action.title}</span>
                    </div>
                  );
                }
                case 'deal':
                  return (
                    <div key={col.key} className={`av-table-cell av-table-cell--truncate ${action.deal ? 'av-table-cell--clickable' : ''}`} onClick={() => action.deal && navToDeal(action)} title={action.deal ? 'Open deal' : ''}>
                      {action.deal ? (
                        <span className="av-table-deal-name">💼 {action.deal.name}</span>
                      ) : action.prospect ? (
                        <span className="av-table-prospect-name">🎯 {action.prospect.firstName} {action.prospect.lastName}</span>
                      ) : (
                        <span className="av-table-empty">—</span>
                      )}
                    </div>
                  );
                case 'contact':
                  return (
                    <div key={col.key} className={`av-table-cell av-table-cell--truncate ${(action.contact || action.prospect) ? 'av-table-cell--clickable' : ''}`} onClick={() => (action.contact || action.prospect) && navToContact(action)} title={action.contact ? 'Open contact' : action.prospect ? 'Open prospect' : ''}>
                      {action.contact ? (
                        <span>👤 {action.contact.firstName} {action.contact.lastName}</span>
                      ) : action.prospect ? (
                        <span>🎯 {action.prospect.firstName} {action.prospect.lastName}</span>
                      ) : (
                        <span className="av-table-empty">—</span>
                      )}
                    </div>
                  );
                case 'due':
                  return (
                    <div key={col.key} className={`av-table-cell ${due.overdue ? 'av-table-cell--overdue' : due.today ? 'av-table-cell--today' : ''}`}>
                      {due.text}
                    </div>
                  );
                case 'priority':
                  return (
                    <div key={col.key} className="av-table-cell">
                      <span className="av-table-priority-dot" style={{ background: pri }} />
                      <span style={{ textTransform: 'capitalize' }}>{action.priority}</span>
                    </div>
                  );
                case 'status':
                  return (
                    <div key={col.key} className={`av-table-cell ${action.status === 'in_progress' ? 'av-table-cell--clickable' : ''}`} onClick={() => action.status === 'in_progress' && navToInProgress(action)} title={action.status === 'in_progress' ? 'Resume — go to where this action is in progress' : ''}>
                      <span className="av-table-status-badge" style={{ background: st.color + '14', color: st.color }}>
                        {st.icon} {st.label}
                      </span>
                    </div>
                  );
                case 'source':
                  return (
                    <div key={col.key} className="av-table-cell">
                      <span className="av-table-source-badge" style={{ background: src.bg, color: src.color, borderColor: src.border }}>
                        {src.label}
                      </span>
                    </div>
                  );
                case 'account':
                  return (
                    <div key={col.key} className="av-table-cell av-table-cell--truncate">
                      {action.deal?.account || action.prospect?.companyName || <span className="av-table-empty">—</span>}
                    </div>
                  );
                case 'stage':
                  return (
                    <div key={col.key} className="av-table-cell">
                      <span className="av-table-stage">{action.deal?.stage?.replace(/_/g, ' ') || action.prospect?.stage?.replace(/_/g, ' ') || '—'}</span>
                    </div>
                  );
                case 'internal':
                  return (
                    <div key={col.key} className="av-table-cell">
                      {action.isInternal ? '🏠 Internal' : '🌐 External'}
                    </div>
                  );
                case 'module': {
                  const m = MODULE_META[action.sourceModule] || MODULE_META.general;
                  return (
                    <div key={col.key} className="av-table-cell">
                      <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 8, background: m.bg, color: m.color }}>
                        {m.icon} {m.label}
                      </span>
                    </div>
                  );
                }
                case 'actions':
                  return (
                    <div key={col.key} className="av-table-cell av-table-cell--actions">
                      {!isComplete && action.status !== 'snoozed' && (
                        <button
                          className="av-table-start-btn"
                          style={{ background: ch.color + '14', color: ch.color, borderColor: ch.color + '40' }}
                          onClick={() => onStart(action)}
                        >
                          {ch.icon} →
                        </button>
                      )}
                      {action.status === 'snoozed' && (
                        <button
                          className="av-table-start-btn"
                          style={{ background: '#8b5cf614', color: '#8b5cf6', borderColor: '#8b5cf640' }}
                          onClick={() => onUnsnooze(refOf(action))}
                        >
                          ↑ Wake
                        </button>
                      )}
                    </div>
                  );
                default:
                  return <div key={col.key} className="av-table-cell">—</div>;
              }
            })}
          </div>
        );
      })}

      {sorted.length === 0 && (
        <div className="av-table-empty-state">No actions match the current filters.</div>
      )}
      </div>{/* end av-table-body */}
    </div>
  );
}


// ── Action Card ───────────────────────────────────────────────────────────────

function ActionCard({ action, onStatusChange, onStart, onSnoozeClick, onUnsnooze }) {
  const dueInfo    = formatDate(action.dueDate);
  const pColor     = PRIORITY_COLORS[action.priority] || PRIORITY_COLORS.medium;
  const isCompleted = action.status === 'completed';
  const isSnoozed   = action.status === 'snoozed';

  return (
    <div
      id={`action-card-${action.id}`}
      className={`
      av-card
      av-card--${action.priority}
      ${isCompleted ? 'av-card--completed' : ''}
      ${isSnoozed   ? 'av-card--snoozed'   : ''}
      ${action.isInternal ? 'av-card--internal' : ''}

    `.trim().replace(/\s+/g, ' ')}>

      {/* Card header */}
      <div className="av-card-header">
        <span className="av-type-badge" style={{ background: pColor + '18', color: pColor }}>
          {nextStepLabel(action.nextStep)}
        </span>
        <div className="av-card-badges">
          {isSnoozed && <span className="av-badge av-badge--snoozed">😴 Snoozed</span>}
          {action.isInternal && <span className="av-badge av-badge--internal">🏠 Internal</span>}
          {action.source === 'ai_generated' && (
            <span className="av-badge av-badge--ai" title={
              action.metadata?.confidence
                ? `AI · ${Math.round(action.metadata.confidence * 100)}% confidence`
                : 'AI Generated'
            }>🤖 AI</span>
          )}
          {action.source === 'playbook' && (
            <span className="av-badge av-badge--playbook" title="Generated from playbook stage guidance"
              style={{ background: '#ebf8ff', color: '#2b6cb0', border: '1px solid #bee3f8' }}>📘 Playbook</span>
          )}
          <span className="av-priority-dot" style={{ background: pColor }} title={action.priority} />
        </div>
      </div>

      {/* Title */}
      <h3 className="av-card-title">{action.title}</h3>

      {/* Deal / Account context */}
      {action.deal && (
        <div className="av-card-context">
          <span className="av-context-deal">💼 {action.deal.name}</span>
          {action.deal.account && <span className="av-context-account">· {action.deal.account}</span>}
          {action.deal.stage && (
            <span className="av-context-stage">{action.deal.stage.replace(/_/g, ' ')}</span>
          )}
        </div>
      )}

      {/* Prospect context */}
      {action.prospect && (
        <div className="av-card-context">
          <span style={{ color: '#0F9D8E', fontWeight: 600 }}>🎯 {action.prospect.firstName} {action.prospect.lastName}</span>
          {action.prospect.companyName && <span className="av-context-account">· {action.prospect.companyName}</span>}
          {action.prospect.stage && (
            <span className="av-context-stage" style={{ background: '#0F9D8E18', color: '#0F9D8E' }}>
              {action.prospect.stage.replace(/_/g, ' ')}
            </span>
          )}
        </div>
      )}

      {/* Contact context */}
      {action.contact && (
        <div className="av-card-context">
          <span>👤 {action.contact.firstName} {action.contact.lastName}</span>
          {action.contact.email && <span className="av-context-email">· {action.contact.email}</span>}
        </div>
      )}

      {/* Due date */}
      {dueInfo && !isSnoozed && (
        <div className={`av-due ${dueInfo.overdue ? 'av-due--overdue' : dueInfo.today ? 'av-due--today' : ''}`}>
          🗓 {dueInfo.text}
        </div>
      )}

      {/* Snooze info banner */}
      {isSnoozed && (
        <div className="av-snooze-info">
          <div className="av-snooze-info__reason">
            💬 {action.snoozeReason}
          </div>
          <div className="av-snooze-info__meta">
            Snoozed {SNOOZE_DURATION_LABELS[action.snoozeDuration] || action.snoozeDuration}
            {action.snoozedUntil && (
              <> · wakes {new Date(action.snoozedUntil).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}</>
            )}
          </div>
          <button className="av-snooze-info__unsnooze" onClick={() => onUnsnooze(refOf(action))}>
            ↑ Unsnooze
          </button>
        </div>
      )}

      {/* Evidence panel — hide on snoozed */}
      {!isSnoozed && <EvidencePanel action={action} />}

      {/* Footer row: status stepper + resume + snooze + export context */}
      {!isSnoozed && (
        <div className="av-card-footer">
          <StatusStepper action={action} onStatusChange={onStatusChange} onStart={onStart} />
          <div className="av-card-footer-actions">
            <ResumeButton action={action} />
            <ExportContextButton action={action} />
            {!isCompleted && (
              <button
                className="av-snooze-btn"
                onClick={() => onSnoozeClick(action)}
                title="Snooze this action"
              >
                😴
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── STRAP Action Status Pill ─────────────────────────────────────────────────
// Option B: pill showing current status + forward-only arrow button.
// Grey = not started, Amber = in progress, Green = completed.

const STRAP_ACTION_STATUS = {
  not_started: { label: 'Not started', pill: { background: '#F1EFE8', color: '#5F5E5A' }, next: 'in_progress' },
  blocked:     { label: 'Blocked',     pill: { background: '#FBE9E7', color: '#C0392B' }, next: 'in_progress' },
  skipped:     { label: 'Skipped',     pill: { background: '#F1EFE8', color: '#9ca3af' }, next: null },
  cancelled:   { label: 'Cancelled',   pill: { background: '#F1EFE8', color: '#9ca3af' }, next: null },
  in_progress:  { label: 'In progress', pill: { background: '#FAEEDA', color: '#854F0B' }, next: 'completed'  },
  completed:    { label: 'Completed',   pill: { background: '#EAF3DE', color: '#3B6D11' }, next: null         },
};

function StrapActionStatusPill({ act, onStatusChanged }) {
  const [updating, setUpdating] = useState(false);
  const cfg = STRAP_ACTION_STATUS[act.status] || STRAP_ACTION_STATUS.not_started;

  async function advance(e) {
    e.stopPropagation();
    if (!cfg.next || updating) return;
    setUpdating(true);
    try {
      const isProspecting = act.action_table === 'prospecting_actions';
      // prospecting_actions uses 'pending' instead of canonical 'not_started' (D19)
      const statusToSend = isProspecting && cfg.next === 'not_started' ? 'pending' : cfg.next;
      const endpoint = isProspecting
        ? `/prospecting-actions/${act.action_id}/status`
        : `/actions/${act.action_id}/status`;
      await apiFetch(endpoint, {
        method: 'PATCH',
        body: JSON.stringify({ status: statusToSend }),
      });
      onStatusChanged(act.action_id, act.action_table, cfg.next);
    } catch (err) {
      alert('Failed to update status: ' + err.message);
    } finally {
      setUpdating(false);
    }
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
      <span style={{
        ...cfg.pill,
        fontSize: 11,
        fontWeight: 500,
        padding: '3px 9px',
        borderRadius: 20,
        whiteSpace: 'nowrap',
        opacity: updating ? 0.6 : 1,
      }}>
        {cfg.label}
      </span>
      {cfg.next && (
        <button
          onClick={advance}
          disabled={updating}
          title={`Advance to ${STRAP_ACTION_STATUS[cfg.next]?.label}`}
          style={{
            width: 22, height: 22, borderRadius: '50%',
            border: '0.5px solid #d1d5db',
            background: 'transparent',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: updating ? 'not-allowed' : 'pointer',
            fontSize: 13, color: '#6b7280', lineHeight: 1, padding: 0,
          }}
        >
          {updating ? '…' : '›'}
        </button>
      )}
    </div>
  );
}

// ── STRAP Pinned Card ────────────────────────────────────────────────────────

function StrapPinnedCard({ strap, expanded, onToggle, onResolve, onReassess, onUpdate }) {
  const [editingSection, setEditingSection] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [resolveMode, setResolveMode] = useState(false);
  const [progress, setProgress] = useState(null);
  const [strapActions, setStrapActions] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [assigningId, setAssigningId] = useState(null);
  const [allDoneConfirm, setAllDoneConfirm] = useState(false);

  const pri = PRIORITY_COLORS[strap.priority] || PRIORITY_COLORS.medium;
  const ent = STRAP_ENTITY_CONFIG[strap.entity_type] || STRAP_ENTITY_CONFIG.deal;
  const ctx = strap.entityContext || {};

  // Fetch progress when expanded (only once per expand)
  const progressFetched = React.useRef(false);
  useEffect(() => {
    if (expanded && !progressFetched.current) {
      progressFetched.current = true;
      apiService.straps.getProgress(strap.id)
        .then(res => setProgress(res.data?.progress || null))
        .catch(() => setProgress(null));
      // Fetch individual STRAP actions with assignees
      apiFetch(`/straps/${strap.id}/actions`)
        .then(data => setStrapActions(data.actions || []))
        .catch(() => setStrapActions([]));
      // Fetch team members for assignee dropdown
      apiFetch('/actions/filter-options?scope=org')
        .then(data => setTeamMembers(data.owners || []))
        .catch(() => setTeamMembers([]));
    }
    if (!expanded) {
      progressFetched.current = false;
    }
  }, [expanded, strap.id]);

  function startEdit(field, currentValue) {
    setEditingSection(field);
    setEditValue(currentValue || '');
  }

  async function saveEdit(field) {
    setSaving(true);
    try {
      await onUpdate(strap.id, { [field]: editValue });
      setEditingSection(null);
    } catch (err) {
      alert('Failed to save: ' + err.message);
    } finally {
      setSaving(false);
    }
  }

  async function handleReassign(actionId, actionTable, newUserId) {
    setAssigningId(actionId);
    try {
      const res = await apiFetch(`/straps/actions/${actionId}/assign`, {
        method: 'PATCH',
        body: JSON.stringify({ userId: parseInt(newUserId), actionTable }),
      });
      if (res.success) {
        // Update local state
        setStrapActions(prev => prev.map(a =>
          a.action_id === actionId ? { ...a, user_id: parseInt(newUserId), assignee_name: res.assignee.name } : a
        ));
      }
    } catch (err) {
      alert('Failed to reassign: ' + err.message);
    } finally {
      setAssigningId(null);
    }
  }

  // Called by StrapActionStatusPill after a successful status update
  function handleStrapActionStatusChanged(actionId, actionTable, newStatus) {
    const updated = strapActions.map(a =>
      a.action_id === actionId && a.action_table === actionTable
        ? { ...a, status: newStatus }
        : a
    );
    setStrapActions(updated);

    // Recompute progress locally so the header bar updates immediately
    const total     = updated.length;
    const completed = updated.filter(a => a.status === 'completed').length;
    const inProg    = updated.filter(a => a.status === 'in_progress').length;
    const pending   = total - completed - inProg;
    const percent   = total > 0 ? Math.round((completed / total) * 100) : 0;
    setProgress({ total, completed, inProgress: inProg, pending, percent });

    // If this was the last action completing, prompt to resolve the STRAP
    if (newStatus === 'completed' && completed === total && total > 0) {
      setAllDoneConfirm(true);
    }
  }

  function navigateToEntity() {
    const tabMap = { deal: 'deals', account: 'accounts', prospect: 'prospecting', implementation: 'deals' };
    const tab = tabMap[strap.entity_type] || 'deals';
    const detail = { tab };
    if (strap.entity_type === 'deal' || strap.entity_type === 'implementation') detail.dealId = strap.entity_id;
    if (strap.entity_type === 'account') detail.accountId = strap.entity_id;
    window.dispatchEvent(new CustomEvent('navigate', { detail }));
  }

  return (
    <div className={`av-strap-card ${expanded ? 'av-strap-card--expanded' : ''}`}
         style={{ borderLeftColor: pri }}>
      {/* Collapsed header */}
      <div className="av-strap-header" onClick={onToggle}>
        <span className="av-strap-icon">S</span>
        <span className="av-strap-priority" style={{ background: pri + '14', color: pri, borderColor: pri + '40' }}>
          {strap.priority}
        </span>
        <span className="av-strap-entity-badge" style={{ background: ent.bg, color: ent.color }}>
          {ent.icon} {strap.entity_type}
        </span>
        <span className="av-strap-hurdle-title">{strap.hurdle_title}</span>
        {progress && progress.total > 0 && (
          <span className="av-strap-progress">
            <span className="av-strap-progress-bar">
              <span className="av-strap-progress-fill"
                style={{ width: `${progress.percent}%`, background: progress.percent === 100 ? '#10b981' : '#4f46e5' }}
              />
            </span>
            <span className="av-strap-progress-label" style={{ color: progress.percent === 100 ? '#059669' : '#6b7280' }}>
              {progress.completed}/{progress.total}
            </span>
          </span>
        )}
        <span className="av-strap-entity-name">{ctx.entityName}</span>
        <span className="av-strap-chevron">{expanded ? '▲' : '▼'}</span>
      </div>

      {/* Expanded body */}
      {expanded && (
        <div className="av-strap-body">
          {/* S-T-R-A sections */}
          {STRAP_SECTIONS.map(sec => {
            const value = strap[sec.field] || '';
            const isEditing = editingSection === sec.field;
            return (
              <div key={sec.key} className="av-strap-section">
                <div className="av-strap-section-header">
                  <span className="av-strap-section-icon" style={{ background: sec.color }}>{sec.key}</span>
                  <span className="av-strap-section-label" style={{ color: sec.color }}>{sec.label}</span>
                  {!isEditing && (
                    <button className="av-strap-edit-btn" onClick={(e) => { e.stopPropagation(); startEdit(sec.field, value); }}>
                      ✎ Edit
                    </button>
                  )}
                </div>
                {isEditing ? (
                  <div className="av-strap-edit-area">
                    <textarea
                      className="av-strap-textarea"
                      value={editValue}
                      onChange={e => setEditValue(e.target.value)}
                      rows={sec.key === 'A' ? 5 : 3}
                      autoFocus
                    />
                    <div className="av-strap-edit-actions">
                      <button className="av-strap-save-btn" onClick={() => saveEdit(sec.field)} disabled={saving}>
                        {saving ? '…' : 'Save'}
                      </button>
                      <button className="av-strap-cancel-btn" onClick={() => setEditingSection(null)}>Cancel</button>
                    </div>
                  </div>
                ) : sec.key === 'A' && strapActions.length > 0 ? (
                  <div className="av-strap-actions-list">
                    {strapActions.map(act => {
                      const chMeta = CHANNEL_META[act.next_step] || CHANNEL_META.email;
                      return (
                        <div key={`${act.action_table}-${act.action_id}`}
                          className={`av-strap-action-row ${act.status === 'completed' ? 'av-strap-action-row--done' : ''}`}>
                          <span className="av-strap-action-channel" style={{ background: chMeta.color + '14', color: chMeta.color }}>
                            {chMeta.icon}
                          </span>
                          <span className={`av-strap-action-title ${act.status === 'completed' ? 'av-strap-action-title--done' : ''}`}>
                            {act.title}
                          </span>
                          {act.due_date && (
                            <span className="av-strap-action-due">
                              {new Date(act.due_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                            </span>
                          )}
                          <StrapActionStatusPill
                            act={act}
                            onStatusChanged={handleStrapActionStatusChanged}
                          />
                          <select
                            className="av-strap-action-assignee"
                            value={act.user_id || ''}
                            onChange={e => handleReassign(act.action_id, act.action_table, e.target.value)}
                            disabled={assigningId === act.action_id}
                            title={act.assignee_name || 'Unassigned'}
                          >
                            <option value="">Unassigned</option>
                            {teamMembers.map(m => (
                              <option key={m.id} value={m.id}>{m.name}</option>
                            ))}
                          </select>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className={`av-strap-section-content ${sec.key === 'A' ? 'av-strap-pre' : ''}`}>
                    {value || <span className="av-strap-empty">Not set</span>}
                  </div>
                )}
              </div>
            );
          })}

          {/* Meta */}
          <div className="av-strap-meta">
            <span>{strap.hurdle_type?.replace(/_/g, ' ')}</span>
            <span>{new Date(strap.created_at).toLocaleDateString()}</span>
            {strap.ai_model && <span>AI: {strap.ai_model}</span>}
            {strap.source === 'manual' && <span className="av-strap-manual-badge">Manual</span>}
            {strap.created_by_name && <span>By: {strap.created_by_name}</span>}
          </div>

          {/* Progress tracking */}
          {progress && progress.total > 0 && (
            <div className="av-strap-progress-section">
              <span className="av-strap-progress-section-label">📋 Action Progress</span>
              <span className="av-strap-progress-bar av-strap-progress-bar--large">
                <span className="av-strap-progress-fill"
                  style={{ width: `${progress.percent}%`, background: progress.percent === 100 ? '#10b981' : '#4f46e5' }}
                />
              </span>
              <span className="av-strap-progress-detail">
                {progress.completed} done · {progress.inProgress} in progress · {progress.pending} to do
              </span>
              {progress.percent === 100 && (
                <span className="av-strap-progress-complete">✓ All actions complete — ready to resolve</span>
              )}
            </div>
          )}

          {/* All-done confirmation — shown when last action is completed */}
          {allDoneConfirm && (
            <div style={{
              background: '#f0fdf4', border: '1px solid #bbf7d0',
              borderRadius: 10, padding: '14px 16px', marginBottom: 12,
              display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
            }}>
              <span style={{ fontSize: 18 }}>🎉</span>
              <span style={{ flex: 1, fontSize: 13, color: '#166534', fontWeight: 500 }}>
                All actions complete — mark this STRAP as resolved?
              </span>
              <button
                onClick={() => { onResolve(strap.id, 'manual', 'All actions completed'); setAllDoneConfirm(false); }}
                style={{
                  padding: '6px 14px', borderRadius: 7, border: 'none',
                  background: '#16a34a', color: '#fff', fontSize: 13,
                  fontWeight: 500, cursor: 'pointer',
                }}
              >
                Yes, Resolve
              </button>
              <button
                onClick={() => setAllDoneConfirm(false)}
                style={{
                  padding: '6px 12px', borderRadius: 7,
                  border: '1px solid #bbf7d0', background: '#fff',
                  color: '#16a34a', fontSize: 13, cursor: 'pointer',
                }}
              >
                Not yet
              </button>
            </div>
          )}

          {/* Footer actions */}
          <div className="av-strap-footer">
            {!resolveMode ? (
              <>
                <button className="av-strap-resolve-btn" onClick={() => setResolveMode(true)}>✓ Resolve</button>
                <button className="av-strap-action-btn" onClick={() => onReassess(strap.id)}>↻ Reassess</button>
                <button className="av-strap-action-btn" onClick={navigateToEntity}>
                  Open {strap.entity_type} →
                </button>
              </>
            ) : (
              <>
                <span className="av-strap-resolve-label">How resolved?</span>
                <button className="av-strap-resolve-opt av-strap-resolve-opt--cleared"
                  onClick={() => { onResolve(strap.id, 'manual', 'Hurdle cleared'); setResolveMode(false); }}>
                  ✓ Hurdle Cleared
                </button>
                <button className="av-strap-resolve-opt av-strap-resolve-opt--superseded"
                  onClick={() => { onResolve(strap.id, 'superseded', 'New hurdle identified'); setResolveMode(false); }}>
                  ↻ Superseded
                </button>
                <button className="av-strap-resolve-opt av-strap-resolve-opt--irrelevant"
                  onClick={() => { onResolve(strap.id, 'manual', 'No longer relevant'); setResolveMode(false); }}>
                  🚫 Not Relevant
                </button>
                <button className="av-strap-cancel-btn" onClick={() => setResolveMode(false)}>Cancel</button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Filter Bar ────────────────────────────────────────────────────────────────

function FilterBar({ filters, onChange, options, scope }) {
  const hasFilters = filters.dealId || filters.accountId || filters.ownerId || filters.dealStage || filters.generatedWithin;

  const stageOptions = options.stages || [];

  return (
    <div className="av-filters">
      {/* Team member — only visible on team/org scope */}
      {(scope === 'team' || scope === 'org') && options.owners?.length > 0 && (
        <select
          className="av-filter-select"
          value={filters.ownerId}
          onChange={e => onChange('ownerId', e.target.value)}
        >
          <option value="">{scope === 'team' ? 'All Team Members' : 'All People'}</option>
          {options.owners.map(o => (
            <option key={o.id} value={o.id}>{o.name}</option>
          ))}
        </select>
      )}

      {/* Deal */}
      <select
        className="av-filter-select"
        value={filters.dealId}
        onChange={e => onChange('dealId', e.target.value)}
      >
        <option value="">All Deals</option>
        {options.deals.map(d => (
          <option key={d.id} value={d.id}>{d.name}</option>
        ))}
      </select>

      {/* Account */}
      <select
        className="av-filter-select"
        value={filters.accountId}
        onChange={e => onChange('accountId', e.target.value)}
      >
        <option value="">All Accounts</option>
        {options.accounts.map(a => (
          <option key={a.id} value={a.id}>{a.name}</option>
        ))}
      </select>

      {/* Deal Stage */}
      {stageOptions.length > 0 && (
        <select
          className="av-filter-select"
          value={filters.dealStage}
          onChange={e => onChange('dealStage', e.target.value)}
        >
          <option value="">All Stages</option>
          {stageOptions.map(s => (
            <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>
          ))}
        </select>
      )}

      {/* Generated within */}
      <select
        className="av-filter-select"
        value={filters.generatedWithin}
        onChange={e => onChange('generatedWithin', e.target.value)}
      >
        <option value="">Generated: All Time</option>
        <option value="12h">Generated: Last 12 hours</option>
        <option value="1d">Generated: Last 1 day</option>
        <option value="1w">Generated: Last 1 week</option>
      </select>

      {/* Module */}
      <select
        className="av-filter-select"
        value={filters.sourceModule}
        onChange={e => onChange('sourceModule', e.target.value)}
      >
        <option value="">All modules</option>
        {Object.entries(MODULE_META).map(([key, cfg]) => (
          <option key={key} value={key}>{cfg.icon} {cfg.label}</option>
        ))}
      </select>

      {/* Clear */}
      {hasFilters && (
        <button className="av-filter-clear" onClick={() => onChange('__reset__', null)}>
          ✕ Clear
        </button>
      )}
    </div>
  );
}

// ── Default filters ───────────────────────────────────────────────────────────

const DEFAULT_FILTERS = {
  source:          'all',
  isInternal:      '',
  actionType:      '',
  nextStep:        '',
  dealId:          '',
  accountId:       '',
  ownerId:         '',
  dueAfter:        '',
  dueBefore:       '',
  status:          '',
  dealStage:       '',
  generatedWithin: '',   // '' = All Time | '12h' | '1d' | '1w'
  sourceModule:    '',
};

// ── Main Component ────────────────────────────────────────────────────────────

// ── Add Action Modal ──────────────────────────────────────────────────────────
// Lets reps create a manual action not generated by any rule or playbook.
// source = 'manual' — distinguished in filtering and UI.

const ACTION_TYPES = [
  { value: 'email_send',       label: '✉️ Email' },
  { value: 'meeting_schedule', label: '📅 Meeting / Call' },
  { value: 'follow_up',        label: '🔄 Follow-Up' },
  { value: 'document_prep',    label: '📄 Document Prep' },
  { value: 'task_complete',    label: '✅ Internal Task' },
  { value: 'review',           label: '🔍 Review' },
];

// Manual actions must always be linked to a deal, account, or contact.
function AddActionModal({ onSave, onClose, deals = [], accounts = [], contacts = [] }) {
  const [title,       setTitle]       = useState('');
  const [actionType,  setActionType]  = useState('follow_up');
  const [priority,    setPriority]    = useState('medium');
  const [description, setDescription] = useState('');
  const [dueDate,     setDueDate]     = useState('');
  const [isInternal,  setIsInternal]  = useState(false);
  const [saving,      setSaving]      = useState(false);

  // Entity linkage — at least one required
  const [linkType,    setLinkType]    = useState('deal');    // 'deal' | 'account' | 'contact'
  const [dealId,      setDealId]      = useState('');
  const [accountId,   setAccountId]   = useState('');
  const [contactId,   setContactId]   = useState('');

  const linkedId = linkType === 'deal' ? dealId : linkType === 'account' ? accountId : contactId;
  const canSave  = title.trim() && linkedId;

  async function handleSave() {
    if (!canSave) return;
    setSaving(true);
    try {
      await onSave({
        title:       title.trim(),
        actionType,
        priority,
        description,
        dueDate,
        isInternal,
        dealId:      linkType === 'deal'    ? dealId    : null,
        accountId:   linkType === 'account' ? accountId : null,
        contactId:   linkType === 'contact' ? contactId : null,
      });
    } finally {
      setSaving(false);
    }
  }

  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  const selectStyle = { width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, boxSizing: 'border-box', background: '#fff' };
  const labelStyle  = { display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 4, color: '#374151' };

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9000 }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{ background: '#fff', borderRadius: 14, padding: 28, width: 500, maxWidth: '94vw', boxShadow: '0 20px 60px rgba(0,0,0,.18)', maxHeight: '90vh', overflowY: 'auto' }}>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h3 style={{ margin: 0, fontSize: 17, fontWeight: 600 }}>+ Add Manual Action</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#6b7280', lineHeight: 1 }}>×</button>
        </div>

        {/* ── Link to deal / account / contact (required) ── */}
        <div style={{ marginBottom: 16, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: '12px 14px' }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 10 }}>
            Link to <span style={{ color: '#ef4444' }}>*</span>
          </div>
          {/* Link type selector */}
          <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
            {[
              { key: 'deal',    label: '💼 Deal',    disabled: deals.length === 0 },
              { key: 'account', label: '🏢 Account', disabled: accounts.length === 0 },
              { key: 'contact', label: '👤 Contact', disabled: contacts.length === 0 },
            ].map(opt => (
              <button
                key={opt.key}
                disabled={opt.disabled}
                onClick={() => { setLinkType(opt.key); setDealId(''); setAccountId(''); setContactId(''); }}
                style={{
                  padding: '5px 12px', borderRadius: 6, border: '1px solid',
                  borderColor: linkType === opt.key ? '#6366f1' : '#e2e8f0',
                  background:  linkType === opt.key ? '#eef2ff' : '#fff',
                  color:       opt.disabled ? '#d1d5db' : linkType === opt.key ? '#4338ca' : '#6b7280',
                  fontSize: 12, fontWeight: 500, cursor: opt.disabled ? 'default' : 'pointer',
                }}
              >
                {opt.label}
              </button>
            ))}
          </div>
          {/* Entity dropdown */}
          {linkType === 'deal' && (
            <select style={selectStyle} value={dealId} onChange={e => setDealId(e.target.value)} autoFocus>
              <option value="">— Select deal —</option>
              {deals.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
          )}
          {linkType === 'account' && (
            <select style={selectStyle} value={accountId} onChange={e => setAccountId(e.target.value)}>
              <option value="">— Select account —</option>
              {accounts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
          )}
          {linkType === 'contact' && (
            <select style={selectStyle} value={contactId} onChange={e => setContactId(e.target.value)}>
              <option value="">— Select contact —</option>
              {contacts.map(c => <option key={c.id} value={c.id}>{c.first_name} {c.last_name}{c.company ? ` · ${c.company}` : ''}</option>)}
            </select>
          )}
        </div>

        {/* Title */}
        <div style={{ marginBottom: 14 }}>
          <label style={labelStyle}>Title <span style={{ color: '#ef4444' }}>*</span></label>
          <input
            style={{ ...selectStyle }}
            placeholder="e.g. Send pricing summary to procurement"
            value={title}
            onChange={e => setTitle(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && canSave) handleSave(); }}
          />
        </div>

        {/* Type + Priority */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
          <div>
            <label style={labelStyle}>Type</label>
            <select style={selectStyle} value={actionType} onChange={e => setActionType(e.target.value)}>
              {ACTION_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>
          <div>
            <label style={labelStyle}>Priority</label>
            <select style={selectStyle} value={priority} onChange={e => setPriority(e.target.value)}>
              <option value="critical">🔴 Critical</option>
              <option value="high">🟠 High</option>
              <option value="medium">🟡 Medium</option>
              <option value="low">🟢 Low</option>
            </select>
          </div>
        </div>

        {/* Due date */}
        <div style={{ marginBottom: 14 }}>
          <label style={labelStyle}>Due Date</label>
          <input type="date" style={selectStyle} value={dueDate} onChange={e => setDueDate(e.target.value)} />
        </div>

        {/* Notes */}
        <div style={{ marginBottom: 14 }}>
          <label style={labelStyle}>Notes / Context</label>
          <textarea
            style={{ ...selectStyle, resize: 'vertical', minHeight: 68 }}
            placeholder="Optional — what's the context or goal for this action?"
            value={description}
            onChange={e => setDescription(e.target.value)}
            rows={3}
          />
        </div>

        {/* Internal flag */}
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#374151', marginBottom: 20, cursor: 'pointer' }}>
          <input type="checkbox" checked={isInternal} onChange={e => setIsInternal(e.target.checked)} />
          Internal task (not a customer-facing action)
        </label>

        {/* Buttons */}
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{ padding: '9px 18px', border: '1px solid #d1d5db', borderRadius: 8, background: '#fff', fontSize: 14, cursor: 'pointer' }}>
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !canSave}
            style={{
              padding: '9px 20px', border: 'none', borderRadius: 8,
              background: saving || !canSave ? '#9ca3af' : '#4f46e5',
              color: '#fff', fontSize: 14, fontWeight: 500,
              cursor: saving || !canSave ? 'not-allowed' : 'pointer',
            }}
          >
            {saving ? 'Creating…' : 'Create Action'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Unified-list identity helpers ────────────────────────────────────────────
// The unified list merges rows from two tables (actions, prospecting_actions)
// whose serial ids can collide. The backend sends a composite `uid`
// ("deal-57" / "prospecting-57"); rows are keyed and updates routed by it.
// refOf falls back to building the same shape locally — and ultimately to the
// bare id — so the view still works against an older backend without uid.
const refOf = (a) => a.uid || (a.actionSource ? `${a.actionSource}-${a.id}` : a.id);

function findActionByRef(list, ref) {
  const hit = list.find(a => refOf(a) === ref);
  if (hit) return hit;
  // Legacy fallback: numeric-id match (older callers / pre-uid rows)
  return list.find(a => a.id === ref);
}

export default function ActionsView({ openActionId, onActionOpened }) {
  const [actions,       setActions]       = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [generating,    setGenerating]    = useState(false);
  const [lastGenerated, setLastGenerated] = useState(null); // Phase 5: last generated timestamp
  const [nextActionToast, setNextActionToast] = useState(null); // Phase 2: gate unlock toast
  const [showAddAction, setShowAddAction] = useState(false); // Item 1: manual action creation

  // ── User preferences — loaded from backend (users.ui_preferences) ────────────
  // Defaults are applied server-side; we store state here for reactive UI.
  // ── Recently Generated — simple filter in FilterBar handles this now ─────

  const [error,         setError]         = useState(null);
  const [filters,       setFilters]       = useState(DEFAULT_FILTERS);
  const [filterOptions, setFilterOptions] = useState({ deals: [], accounts: [], owners: [], stages: [] });
  const [contacts,      setContacts]      = useState([]);
  const [deals,         setDeals]         = useState([]);

  // Modal states
  const [composerAction, setComposerAction] = useState(null);
  const [logAction,      setLogAction]      = useState(null);
  const [snoozeAction,   setSnoozeAction]   = useState(null);

  // ── Scope toggle state ────────────────────────────────────────
  const [scope, setScope] = useState('mine');   // 'mine' | 'team' | 'org'
  const actionSource = 'all'; // simplified — column filters handle source filtering
  const isMobile = useIsMobile();
  // Table layout needs ~820px of fixed columns plus a flexible title column;
  // below the breakpoint the card layout is the only readable one, so it is
  // the default there and the layout toggle is hidden (see .av-layout-toggle).
  const [viewLayout, setViewLayout] = useState(
    () => (typeof window !== 'undefined' && window.innerWidth < 768 ? 'cards' : 'table')
  ); // 'table' | 'cards'
  const [visibleCols, setVisibleCols] = useState(() =>
    ALL_COLUMNS.filter(c => c.defaultVisible).map(c => c.key)
  );
  const [hasTeam, setHasTeam] = useState(false);

  // Handles the case where the viewport narrows after mount (rotation, split
  // view, a resized desktop window). Without this the toggle disappears but the
  // layout stays on 'table', leaving unlabelled wrapped cells with no way back.
  useEffect(() => {
    if (isMobile) setViewLayout('cards');
  }, [isMobile]);

  // ── STRAP state ────────────────────────────────────────────────
  const [straps, setStraps]               = useState([]);
  const [strapsLoading, setStrapsLoading] = useState(false);
  const [expandedStrap, setExpandedStrap] = useState(null);

  useEffect(() => {
    apiService.orgAdmin.getMyTeam()
      .then(r => setHasTeam(r.data.hasTeam))
      .catch(() => setHasTeam(false));
  }, []);

  // ── Fetch filter options, contacts, deals when scope changes ──
  const lastScopeFetch = React.useRef('');
  useEffect(() => {
    if (lastScopeFetch.current === scope) return;
    lastScopeFetch.current = scope;

    // Clear person filter when switching back to mine
    if (scope === 'mine') {
      setFilters(prev => ({ ...prev, ownerId: '' }));
    }

    apiFetch(`/actions/filter-options?scope=${scope}`).then(data => setFilterOptions(data)).catch(() => {});
    apiFetch(`/contacts?scope=${scope}`).then(d => setContacts(d.contacts || [])).catch(() => {});
    apiFetch(`/deals?scope=${scope}`).then(d => setDeals(d.deals || [])).catch(() => {});
  }, [scope]);

  // ── Fetch STRAPs ────────────────────────────────────────────────
  // Uses a ref to avoid re-creating the callback and to skip duplicate fetches
  const strapsFetchKey = `${scope}|${actionSource}`;
  const lastStrapsFetch = React.useRef('');

  useEffect(() => {
    // Skip if we already fetched for this exact key
    if (lastStrapsFetch.current === strapsFetchKey) return;
    lastStrapsFetch.current = strapsFetchKey;

    let cancelled = false;
    setStrapsLoading(true);

    const params = { scope };
    if (actionSource === 'deals')       params.entityType = 'deal';
    if (actionSource === 'prospecting') params.entityType = 'prospect';

    apiService.straps.getAllActive(scope, params)
      .then(res => {
        if (!cancelled) setStraps(res.data?.straps || res.data || []);
      })
      .catch(err => {
        if (!cancelled) {
          console.error('Failed to fetch STRAPs:', err);
          setStraps([]);
        }
      })
      .finally(() => {
        if (!cancelled) setStrapsLoading(false);
      });

    return () => { cancelled = true; };
  }, [strapsFetchKey, scope, actionSource]);

  // ── Filter STRAPs client-side to match active action filters ────
  const filteredStraps = straps.filter(s => {
    if (filters.ownerId) {
      const oid = parseInt(filters.ownerId);
      if (s.created_by !== oid) return false;
    }
    if (filters.dealId) {
      const did = parseInt(filters.dealId);
      if (s.entity_type === 'deal' && s.entity_id !== did) return false;
      if (s.entity_type === 'account' && s.entityContext?.accountId !== did) return false;
      if (s.entity_type !== 'deal' && s.entity_type !== 'account') return false;
    }
    if (filters.accountId) {
      const aid = parseInt(filters.accountId);
      if (s.entity_type === 'account' && s.entity_id !== aid) return false;
      if (s.entity_type === 'deal' && s.entityContext?.accountId !== aid) return false;
      if (s.entity_type !== 'deal' && s.entity_type !== 'account') return false;
    }
    return true;
  });

  // ── STRAP actions: resolve, reassess, update ────────────────────
  async function handleStrapResolve(strapId, resolutionType, note) {
    try {
      await apiService.straps.resolve(strapId, { resolutionType, note });
      setStraps(prev => prev.filter(s => s.id !== strapId));
      setExpandedStrap(null);
    } catch (err) {
      alert('Failed to resolve STRAP: ' + err.message);
    }
  }

  async function handleStrapReassess(strapId) {
    try {
      const res = await apiService.straps.reassess(strapId);
      const newStrap = res.data?.strap;
      if (newStrap) {
        setStraps(prev => prev.map(s => s.id === strapId ? { ...newStrap, entityContext: s.entityContext } : s));
      } else {
        setStraps(prev => prev.filter(s => s.id !== strapId));
      }
    } catch (err) {
      alert('Failed to reassess STRAP: ' + err.message);
    }
  }

  async function handleStrapUpdate(strapId, updates) {
    const res = await apiService.straps.update(strapId, updates);
    const updated = res.data?.strap;
    if (updated) {
      setStraps(prev => prev.map(s => s.id === strapId ? { ...s, ...updated } : s));
    }
  }

  // ── Fetch actions (unified) ───────────────────────────────────
  // Uses a key-based dedup to prevent duplicate fetches on re-renders.
  // actionsFetchKey changes only when scope, actionSource, or filters actually change.
  const actionsFetchKey = `${scope}|${actionSource}|${JSON.stringify(filters)}`;
  const lastActionsFetch = React.useRef('');

  const fetchActionsImpl = useCallback(async (activeFilters) => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();

      // Pass scope to backend
      params.set('scope', scope);

      if (activeFilters.status) {
        params.set('status', activeFilters.status);
      }
      if (activeFilters.dealId)     params.set('dealId',     activeFilters.dealId);
      if (activeFilters.accountId)  params.set('accountId',  activeFilters.accountId);
      if (activeFilters.ownerId)    params.set('ownerId',     activeFilters.ownerId);
      if (activeFilters.actionType) params.set('actionType', activeFilters.actionType);
      if (activeFilters.isInternal) params.set('isInternal', activeFilters.isInternal);
      if (activeFilters.nextStep)   params.set('nextStep',   activeFilters.nextStep);
      if (activeFilters.dueAfter)   params.set('dueAfter',   activeFilters.dueAfter);
      if (activeFilters.dueBefore)  params.set('dueBefore',  activeFilters.dueBefore);

      const data = await apiFetch(`/actions/unified?${params.toString()}`);
      let rows = data.actions || [];

      // Client-side module filter
      if (activeFilters.sourceModule) rows = rows.filter(a => a.sourceModule === activeFilters.sourceModule);

      // Client-side source filter
      if (activeFilters.source === 'ai')       rows = rows.filter(a => a.source === 'ai_generated');
      if (activeFilters.source === 'rules')    rows = rows.filter(a => a.source === 'auto_generated');
      if (activeFilters.source === 'playbook') rows = rows.filter(a => a.source === 'playbook');

      // Deals vs Prospecting source filter (uses actionSource from unified endpoint)
      if (actionSource === 'deals')       rows = rows.filter(a => a.actionSource === 'deal');
      if (actionSource === 'prospecting') rows = rows.filter(a => a.actionSource === 'prospecting');

      // Default view: hide completed and snoozed
      if (!activeFilters.status) {
        rows = rows.filter(a => a.status !== 'completed' && a.status !== 'snoozed');
      }

      // Client-side stage filter (deal_stage already on every action row)
      if (activeFilters.dealStage) {
        rows = rows.filter(a =>
          (a.deal?.stage || a.dealStage || a.deal_stage) === activeFilters.dealStage
        );
      }

      // Client-side generated-within filter
      if (activeFilters.generatedWithin) {
        const ms = { '12h': 12 * 60 * 60 * 1000, '1d': 24 * 60 * 60 * 1000, '1w': 7 * 24 * 60 * 60 * 1000 };
        const cutoff = Date.now() - (ms[activeFilters.generatedWithin] || 0);
        rows = rows.filter(a => {
          const ts = a.createdAt ? new Date(a.createdAt).getTime() : null;
          return ts && ts >= cutoff;
        });
      }

      // Derive unique stage values for the FilterBar dropdown
      const stageVals = Array.from(
        new Set(rows.map(a => a.deal?.stage || a.dealStage || a.deal_stage).filter(Boolean))
      ).sort();
      setFilterOptions(prev => ({ ...prev, stages: stageVals }));

      setActions(rows);
    } catch (err) {
      console.error('Error fetching actions:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [scope, actionSource]);

  // Expose fetchActions for manual re-fetches (e.g. after generate)
  const fetchActions = useCallback((f) => {
    lastActionsFetch.current = ''; // force re-fetch
    return fetchActionsImpl(f);
  }, [fetchActionsImpl]);

  useEffect(() => {
    // Skip if we already fetched for this exact combination
    if (lastActionsFetch.current === actionsFetchKey) return;
    lastActionsFetch.current = actionsFetchKey;

    fetchActionsImpl(filters);
  }, [actionsFetchKey, filters, fetchActionsImpl]);

  // Phase 4: scroll to and highlight a specific action when navigating from Calendar
  useEffect(() => {
    if (!openActionId || loading) return;
    const el = document.getElementById(`action-card-${openActionId}`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el.classList.add('av-card--highlighted');
      setTimeout(() => el.classList.remove('av-card--highlighted'), 2500);
    }
    if (onActionOpened) onActionOpened();
  }, [openActionId, loading]); // eslint-disable-line react-hooks/exhaustive-deps

  function handleFilterChange(key, value) {
    if (key === '__reset__') { setFilters(DEFAULT_FILTERS); return; }
    setFilters(prev => ({ ...prev, [key]: value }));
  }

  async function handleStatusChange(actionRef, newStatus) {
    try {
      // Resolve via composite uid (exact across both source tables); the
      // legacy numeric-id path inside findActionByRef keeps old callers
      // working but can mis-resolve on an id collision — all internal call
      // sites now pass refOf(action).
      const action = findActionByRef(actions, actionRef);
      const apiId = action?.id
        ?? (typeof actionRef === 'string' ? actionRef.split('-').pop() : actionRef);
      const targetRef = action ? refOf(action) : actionRef;
      const isTarget = (a) => refOf(a) === targetRef;
      const isProspecting = action?.actionSource === 'prospecting';

      let nextAction = null;

      if (isProspecting) {
        const statusMap = { not_started: 'pending', in_progress: 'in_progress', completed: 'completed' };
        const mappedStatus = statusMap[newStatus] || newStatus;
        await apiFetch(`/prospecting-actions/${apiId}/status`, {
          method: 'PATCH',
          body: JSON.stringify({ status: mappedStatus }),
        });
      } else {
        const result = await apiFetch(`/actions/${apiId}/status`, {
          method: 'PATCH',
          body: JSON.stringify({ status: newStatus }),
        });
        // Phase 2: gate unlock — next action returned by backend
        if (result.nextAction) {
          nextAction = result.nextAction;
        }
      }

      setActions(prev => {
        let updated = prev.map(a => isTarget(a)
          ? { ...a, status: newStatus, completed: newStatus === 'completed' }
          : a
        ).filter(a => {
          if (!filters.status && isTarget(a) && newStatus === 'completed') return false;
          return true;
        });

        // Append the unlocked next action if returned
        if (nextAction) {
          // Map backend row to frontend shape
          updated = [...updated, {
            id:              nextAction.id,
            uid:             `deal-${nextAction.id}`,
            actionSource:    'deal',
            type:            nextAction.type,
            actionType:      nextAction.action_type || nextAction.type,
            priority:        nextAction.priority,
            title:           nextAction.title,
            description:     nextAction.description,
            suggestedAction: nextAction.suggested_action,
            source:          nextAction.source,
            sourceRule:      nextAction.source_rule,
            nextStep:        nextAction.next_step || 'email',
            status:          'not_started',
            dueDate:         nextAction.due_date,
            deal:            action?.deal || null,
            contact:         null,
            prospect:        null,
          }];
        }
        return updated;
      });

      // Show toast for unlocked next action
      if (nextAction) {
        setNextActionToast(nextAction.title);
        setTimeout(() => setNextActionToast(null), 4000);
      }
    } catch (err) {
      console.error('Status update failed:', err);
      alert('Failed to update status: ' + err.message);
    }
  }

  function handleStart(action) {
    const nextStep = action.nextStep || 'email';
    const emailNextSteps = ['email', 'follow_up'];

    if (emailNextSteps.includes(nextStep)) {
      // Email → opens composer (existing flow)
      setComposerAction(action);
      return;
    }

    // For all other channels: set status to in_progress, then open resource
    const person = action.contact || action.prospect || {};
    const dealId = action.deal?.id || null;

    // Attempt external navigation based on channel
    if (nextStep === 'call') {
      const phone = person.phone;
      if (phone) {
        window.open(`tel:${phone.replace(/\s/g, '')}`, '_self');
      }
    } else if (nextStep === 'linkedin') {
      const url = person.linkedinUrl;
      if (url) {
        // noopener: linkedinUrl is prospect-sourced data — don't hand the
        // opened page a window.opener reference (reverse tabnabbing).
        window.open(url.startsWith('http') ? url : `https://${url}`, '_blank', 'noopener,noreferrer');
      }
    } else if (nextStep === 'whatsapp') {
      const phone = person.phone;
      if (phone) {
        const cleanPhone = phone.replace(/[\s\-()]/g, '').replace(/^\+/, '');
        window.open(`https://wa.me/${cleanPhone}`, '_blank', 'noopener,noreferrer');
      }
    } else if (nextStep === 'document') {
      // Best: go to deal detail page which has DealFilesPanel with deal-specific files
      if (dealId) {
        window.dispatchEvent(new CustomEvent('navigate', {
          detail: { tab: 'deals', dealId, resume: true },
        }));
      } else if (action.deal?.account) {
        // Account-level STRAP: go to accounts tab which shows all related deals
        const accountId = action.deal?.accountId || null;
        if (accountId) {
          window.dispatchEvent(new CustomEvent('navigate', {
            detail: { tab: 'accounts', accountId, resume: true },
          }));
        } else {
          window.dispatchEvent(new CustomEvent('navigate', {
            detail: { tab: 'files', resume: true },
          }));
        }
      } else {
        window.dispatchEvent(new CustomEvent('navigate', {
          detail: { tab: 'files', resume: true },
        }));
      }
    }

    // Set status to in_progress (async, non-blocking)
    handleStatusChange(refOf(action), 'in_progress').catch(() => {});

    // Open log modal so they can record the outcome
    setLogAction(action);
  }

  async function handleEmailSent(emailData) {
    try {
      const result = await apiFetchRaw('/emails', {
        method: 'POST',
        body: JSON.stringify({
          dealId:    emailData.deal_id    || composerAction?.deal?.id || null,
          contactId: emailData.contact_id || null,
          subject:   emailData.subject,
          body:      emailData.body,
          toAddress: emailData.toAddress,
          actionId:  emailData.actionId  || null,
        }),
      });
      if (emailData.actionId) {
        setActions(prev => prev.map(a =>
          a.id === emailData.actionId
            ? { ...a, status: a.status === 'not_started' ? 'in_progress' : a.status }
            : a
        ));
      }
      setComposerAction(null);
      return result;
    } catch (err) {
      console.error('Email send failed:', err);
      throw err;
    }
  }

  async function handleManualComplete(action, notes) {
    try {
      await apiFetch(`/actions/${action.id}/status`, {
        method: 'PATCH',
        body: JSON.stringify({ status: 'completed', notes }),
      });
      setActions(prev => prev
        .map(a => a.id === action.id ? { ...a, status: 'completed', completed: true } : a)
        .filter(a => !(!filters.status && a.id === action.id))
      );
      setLogAction(null);
    } catch (err) {
      console.error('Manual complete failed:', err);
      alert('Failed to mark action complete: ' + err.message);
    }
  }

  async function handleManualInProgress(action, notes) {
    try {
      await apiFetch(`/actions/${action.id}/status`, {
        method: 'PATCH',
        body: JSON.stringify({ status: 'in_progress', notes }),
      });
      setActions(prev => prev.map(a =>
        a.id === action.id ? { ...a, status: 'in_progress' } : a
      ));
      setLogAction(null);
    } catch (err) {
      console.error('Status update failed:', err);
    }
  }

  async function handleSnooze(actionRef, reason, duration) {
    const action = findActionByRef(actions, actionRef);
    const apiId = action?.id
      ?? (typeof actionRef === 'string' ? actionRef.split('-').pop() : actionRef);
    const targetRef = action ? refOf(action) : actionRef;
    const isTarget = (a) => refOf(a) === targetRef;
    const endpoint = action?.actionSource === 'prospecting'
      ? `/prospecting-actions/${apiId}/snooze`
      : `/actions/${apiId}/snooze`;
    await apiFetch(endpoint, {
      method: 'PATCH',
      body: JSON.stringify({ reason, duration }),
    });
    // Remove from open view, show in snoozed view
    setActions(prev =>
      prev.map(a => isTarget(a)
        ? { ...a, status: 'snoozed', snoozeReason: reason, snoozeDuration: duration }
        : a
      ).filter(a => {
        if (filters.status !== 'snoozed' && isTarget(a)) return false;
        return true;
      })
    );
    setSnoozeAction(null);
  }

  async function handleUnsnooze(actionRef) {
    try {
      const action = findActionByRef(actions, actionRef);
      const apiId = action?.id
        ?? (typeof actionRef === 'string' ? actionRef.split('-').pop() : actionRef);
      const targetRef = action ? refOf(action) : actionRef;
      const isTarget = (a) => refOf(a) === targetRef;
      const endpoint = action?.actionSource === 'prospecting'
        ? `/prospecting-actions/${apiId}/unsnooze`
        : `/actions/${apiId}/unsnooze`;
      await apiFetch(endpoint, { method: 'PATCH' });
      setActions(prev =>
        prev.map(a => isTarget(a)
          ? { ...a, status: 'not_started', snoozedUntil: null, snoozeReason: null, snoozeDuration: null }
          : a
        ).filter(a => {
          // If we're on snoozed view, remove it after unsnooze
          if (filters.status === 'snoozed' && isTarget(a)) return false;
          return true;
        })
      );
    } catch (err) {
      alert('Failed to unsnooze: ' + err.message);
    }
  }

  async function handleGenerateActions() {
    setGenerating(true);
    try {
      const result = await apiFetch('/actions/generate', { method: 'POST' });
      const generatedAt = new Date();
      await fetchActions(filters);
      setLastGenerated(generatedAt);
      const dealMsg     = result.deal        ? `${result.deal.inserted} deal`        : '';
      const prospectMsg = result.prospecting ? `${result.prospecting.created} prospecting` : '';
      const clmMsg      = result.clm         ? `${result.clm.inserted} CLM`          : '';
      const parts = [dealMsg, prospectMsg, clmMsg].filter(Boolean).join(', ');
      alert(`✅ Generated ${parts || 'no new'} action(s).${result.prospecting?.skipped ? ` Skipped ${result.prospecting.skipped} duplicate(s).` : ''}`);
    } catch (err) {
      alert('Failed to generate actions: ' + err.message);
    } finally {
      setGenerating(false);
    }
  }

  // Item 1: Manual action creation
  async function handleAddAction(formData) {
    try {
      const result = await apiFetch('/actions', {
        method: 'POST',
        body: JSON.stringify({
          title:       formData.title,
          type:        formData.actionType,
          priority:    formData.priority,
          description: formData.description || null,
          dueDate:     formData.dueDate     || null,
          isInternal:  formData.isInternal  || false,
          dealId:      formData.dealId      || null,
          contactId:   formData.contactId   || null,
        }),
      });
      setShowAddAction(false);
      // Append to current list immediately
      if (result.action) {
        setActions(prev => [{
          id:           result.action.id,
          actionSource: 'deal',
          type:         result.action.type,
          actionType:   result.action.action_type || result.action.type,
          priority:     result.action.priority,
          title:        result.action.title,
          description:  result.action.description,
          status:       'not_started',
          source:       'manual',
          nextStep:     result.action.next_step || 'email',
          dueDate:      result.action.due_date,
          isInternal:   result.action.is_internal,
          deal:         null,
          contact:      null,
          prospect:     null,
        }, ...prev]);
      }
    } catch (err) {
      alert('Failed to create action: ' + err.message);
    }
  }
  const yetToStart = actions.filter(a => a.status === 'not_started').length;
  const inProgress = actions.filter(a => a.status === 'in_progress').length;
  const snoozed    = actions.filter(a => a.status === 'snoozed').length;
  const completed  = actions.filter(a => a.status === 'completed').length;

  return (
    <>
      {/* Phase 2: Gate unlock toast — appears when completing a gate action unlocks the next one */}
      {nextActionToast && (
        <div style={{
          position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)',
          background: '#1e293b', color: '#f8fafc', padding: '12px 20px',
          borderRadius: 10, fontSize: 14, fontWeight: 500, zIndex: 9999,
          boxShadow: '0 4px 20px rgba(0,0,0,.25)',
          display: 'flex', alignItems: 'center', gap: 10, maxWidth: 460,
          animation: 'fadeInUp .25s ease',
        }}>
          <span>🔓</span>
          <span>Next action ready: <strong>{nextActionToast}</strong></span>
          <button
            onClick={() => setNextActionToast(null)}
            style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: 16, marginLeft: 4 }}
          >×</button>
        </div>
      )}

      <div className="av-root">

        {/* Header */}
        <div className="av-header">
          <div className="av-header-left">
            <h2 className="av-title">⚡ Actions</h2>
            <div className="av-header-counts">
              <span className="av-count av-count--open">{yetToStart} to start</span>
              {inProgress > 0 && <span className="av-count av-count--progress">{inProgress} in progress</span>}
              {snoozed    > 0 && <span className="av-count av-count--snoozed">{snoozed} snoozed</span>}
              {completed  > 0 && <span className="av-count av-count--done">{completed} completed</span>}
              {scope !== 'mine' && (
                <span className="av-count" style={{ background: '#eef2ff', color: '#4338ca' }}>
                  {scope === 'team' ? '👥 Team' : '🏢 All Org'}
                </span>
              )}
            </div>
          </div>
          <div className="av-header-right" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {/* Scope toggle — only visible if user has subordinates */}
            {hasTeam && (
              <div style={{
                display: 'inline-flex', borderRadius: '8px', overflow: 'hidden',
                border: '1px solid #e2e4ea', fontSize: '13px',
              }}>
                {['mine', 'team', 'org'].map(s => (
                  <button
                    key={s}
                    onClick={() => setScope(s)}
                    style={{
                      padding: '6px 14px', border: 'none', cursor: 'pointer',
                      background: scope === s ? '#4f46e5' : '#fff',
                      color: scope === s ? '#fff' : '#4b5563',
                      fontWeight: scope === s ? 600 : 400,
                      transition: 'all 0.15s',
                    }}
                  >
                    {s === 'mine' ? 'My Actions' : s === 'team' ? 'My Team' : 'All Org'}
                  </button>
                ))}
              </div>
            )}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2 }}>
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  className="av-generate-btn"
                  onClick={handleGenerateActions}
                  disabled={generating || loading}
                >
                  {generating ? '⏳ Generating…' : '⚡ Generate Actions'}
                </button>
                <button
                  className="av-add-btn"
                  onClick={() => setShowAddAction(true)}
                  title="Create a manual action"
                >
                  + Add Action
                </button>
              </div>
              {lastGenerated && (
                <span style={{ fontSize: 11, color: '#9ca3af' }}>
                  Last run: {lastGenerated.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  {' · '}Auto-runs daily at 6am
                </span>
              )}
            </div>
            <button onClick={() => csvExport(actions, EXPORT_COLUMNS.actions, `actions-${scope}-${new Date().toISOString().slice(0,10)}.csv`)} title="Export CSV"
              style={{ padding: '6px 14px', borderRadius: 8, border: '1px solid #d1d5db',
                       background: '#fff', fontSize: 13, cursor: 'pointer' }}>
              📤 Export
            </button>
            <div className="av-layout-toggle" style={{
              display: 'inline-flex', borderRadius: 8, overflow: 'hidden',
              border: '1px solid #e2e4ea', fontSize: 13,
            }}>
              <button
                onClick={() => setViewLayout('table')}
                title="Table view"
                style={{
                  padding: '6px 12px', border: 'none', cursor: 'pointer',
                  background: viewLayout === 'table' ? '#4f46e5' : '#fff',
                  color: viewLayout === 'table' ? '#fff' : '#4b5563',
                  fontWeight: viewLayout === 'table' ? 600 : 400,
                }}>
                ☰
              </button>
              <button
                onClick={() => setViewLayout('cards')}
                title="Card view"
                style={{
                  padding: '6px 12px', border: 'none', cursor: 'pointer',
                  background: viewLayout === 'cards' ? '#4f46e5' : '#fff',
                  color: viewLayout === 'cards' ? '#fff' : '#4b5563',
                  fontWeight: viewLayout === 'cards' ? 600 : 400,
                }}>
                ▦
              </button>
            </div>
          </div>
        </div>

        {/* Filters */}
        <FilterBar filters={filters} onChange={handleFilterChange} options={filterOptions} scope={scope} />

        {/* ── STRAP Pinned Section ──────────────────────────────────── */}
        {(filters.source === 'all' || filters.source === 'strap') && filteredStraps.length > 0 && (
          <div className="av-strap-section">
            <div className="av-strap-section-header">
              <div className="av-strap-section-title">
                <span>🎯 Active STRAPs</span>
                <span className="av-strap-count">{filteredStraps.length}</span>
              </div>
            </div>
            <div className="av-strap-list">
              {filteredStraps.map(s => (
                <StrapPinnedCard
                  key={s.id}
                  strap={s}
                  expanded={expandedStrap === s.id}
                  onToggle={() => setExpandedStrap(expandedStrap === s.id ? null : s.id)}
                  onResolve={handleStrapResolve}
                  onReassess={handleStrapReassess}
                  onUpdate={handleStrapUpdate}
                />
              ))}
            </div>
          </div>
        )}

        {/* No STRAPs when filtered */}
        {filters.source === 'strap' && filteredStraps.length === 0 && !strapsLoading && (
          <div className="av-empty">
            <div className="av-empty-icon">🎯</div>
            <p>No active STRAPs{filters.dealId || filters.accountId ? ' matching filters' : ''}.</p>
            <p style={{ fontSize: 13, color: '#9ca3af' }}>Generate STRAPs from deal, account, or prospect detail pages.</p>
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div className="av-loading">
            <div className="av-spinner"></div>
            <span>Loading actions…</span>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="av-error">
            <p>{error}</p>
            <button onClick={() => fetchActions(filters)}>Retry</button>
          </div>
        )}

        {/* Empty state */}
        {!loading && !error && actions.length === 0 && filters.source !== 'strap' && (
          <div className="av-empty">
            <div className="av-empty-icon">
              {filters.status === 'snoozed' ? '😴' : '🎯'}
            </div>
            <p>
              {filters.status === 'snoozed'
                ? 'No snoozed actions.'
                : 'No actions match the current filters.'}
            </p>
            {!filters.status && (
              <button className="av-generate-btn" onClick={handleGenerateActions} disabled={generating}>
                Generate Actions
              </button>
            )}
          </div>
        )}

        {/* Action list — hidden when source filter is strap only */}
        {!loading && !error && actions.length > 0 && filters.source !== 'strap' && (
          viewLayout === 'table' ? (
            <ActionsTable
              actions={actions}
              onStatusChange={handleStatusChange}
              onStart={handleStart}
              onSnoozeClick={setSnoozeAction}
              onUnsnooze={handleUnsnooze}
              visibleCols={visibleCols}
              setVisibleCols={setVisibleCols}
            />
          ) : (
            <div className="av-grid">
              {actions.map(action => (
                <ActionCard
                  key={refOf(action)}
                  action={action}
                  onStatusChange={handleStatusChange}
                  onStart={handleStart}
                  onSnoozeClick={setSnoozeAction}
                  onUnsnooze={handleUnsnooze}
                />
              ))}
            </div>
          )
        )}

      </div>

      {/* Email Composer Modal */}
      {composerAction && (
        <EmailComposer
          contacts={contacts}
          deals={deals}
          prefill={{
            contactId: composerAction.contact ? composerAction.contact.id    : null,
            dealId:    composerAction.deal    ? composerAction.deal.id       : null,
            subject:   composerAction.title,
            body:      composerAction.suggestedAction
                         ? 'Hi,\n\n' + composerAction.suggestedAction + '\n\nBest regards,'
                         : '',
            toAddress: composerAction.contact ? composerAction.contact.email : '',
          }}
          actionId={composerAction.id}
          actionContext={{
            title:           composerAction.title,
            suggestedAction: composerAction.suggestedAction,
            nextStep:        composerAction.nextStep,
          }}
          onSubmit={handleEmailSent}
          onClose={() => setComposerAction(null)}
        />
      )}

      {/* Manual Log Modal */}
      {logAction && (
        <ManualLogModal
          action={logAction}
          onComplete={notes   => handleManualComplete(logAction, notes)}
          onInProgress={notes => handleManualInProgress(logAction, notes)}
          onClose={() => setLogAction(null)}
        />
      )}

      {/* Snooze Modal */}
      {snoozeAction && (
        <SnoozeModal
          action={snoozeAction}
          onSnooze={(_id, reason, duration) => handleSnooze(refOf(snoozeAction), reason, duration)}
          onClose={() => setSnoozeAction(null)}
        />
      )}
      {showAddAction && (
        <AddActionModal
          onSave={handleAddAction}
          onClose={() => setShowAddAction(false)}
          deals={deals}
          accounts={filterOptions.accounts || []}
          contacts={contacts}
        />
      )}
    </>
  );
}
